// KPIs › Chart (`/c/{slug}/kpis/chart`, M1-08; KPIS-MODE 2026-10-22, was the Chart mode): the org
// chart editor with its overlay in the URL (`?overlay=kpi|money`) and its lens (`?lens=`,
// CHART-LENSES), the seat page (`/kpis/chart/seat/{id}`, which writes the seat to the playhead,
// `?tab=`), and the old `/kpis/chart/editor` being-built page, now the editor itself
// (redirected). The `chart` gate holds the old mode's rules: every member.
import { createRoute, redirect, useNavigate } from "@tanstack/react-router";
import { isLens, type Lens } from "../features/chart/chart-lenses";
import { ChartModeRoot } from "../features/chart/chart-parts";
import { ChartScreen, OVERLAYS, type Overlay } from "../features/chart/chart-screen";
import { SEAT_TAB_ALIASES, SEAT_TABS, SeatPage, type SeatTab } from "../features/chart/seat-page";
import {
  FounderChart,
  type FounderLens,
  isFounderLens,
} from "../features/portfolio/portfolio-chart";
import { useCompany } from "../features/use-feature";
import { reconcileHref, validateFocusSearch } from "../focus/focus";
import { ModeGate } from "../shell/mode-gate";
import { kpisRoute } from "./c.$slug.kpis";

const ID = /^[0-9a-f-]{36}$/i;

export const chartRoute = createRoute({
  getParentRoute: () => kpisRoute,
  path: "chart",
});

export const chartIndexRoute = createRoute({
  getParentRoute: () => chartRoute,
  path: "/",
  validateSearch: (search: Record<string, unknown>) => ({
    ...validateFocusSearch(search),
    ...((OVERLAYS as readonly unknown[]).includes(search.overlay) && search.overlay !== "structure"
      ? { overlay: search.overlay as Overlay }
      : {}),
    // FOUNDER-OS phase 1: the founder chart's lenses (Health, Money, Headcount, Priorities).
    ...(isLens(search.lens) || isFounderLens(search.lens)
      ? { lens: search.lens as Lens | FounderLens }
      : {}),
    // PRIORITIES-HOME: a seat whose panel opens (Priorities' "Open on the chart").
    ...(typeof search.seat === "string" && ID.test(search.seat) ? { seat: search.seat } : {}),
    // STRUCTURE-CHANGED §3.1: a move staged elsewhere (a DE's "Reports to…"): seat `move` under
    // `under` (a seat, or "top"), added to the chart's draft and reviewed there.
    ...(typeof search.move === "string" &&
    ID.test(search.move) &&
    typeof search.under === "string" &&
    (search.under === "top" || ID.test(search.under))
      ? { move: search.move, under: search.under }
      : {}),
  }),
  component: function Chart() {
    const { api } = chartIndexRoute.useRouteContext();
    const { overlay, lens, seat, move, under } = chartIndexRoute.useSearch();
    const navigate = useNavigate();
    const company = useCompany();
    // FOUNDER-OS phase 1: in Founder OS the chart is the founder chart (linked CEO cards).
    if (company.kind === "portfolio")
      return (
        <ChartModeRoot>
          <ModeGate modeKey="chart">
            <FounderChart
              api={api}
              company={company}
              {...(isFounderLens(lens) ? { lens } : {})}
              onLens={(next) =>
                void navigate({
                  to: ".",
                  search: (prev: Record<string, unknown>) => ({ ...prev, lens: next }),
                  replace: true,
                } as never)
              }
            />
          </ModeGate>
        </ChartModeRoot>
      );
    // The org chart fills the main pane (its canvas fits the tree to the space and scrolls
    // itself, like the chat view), so the mode root is a full-height column.
    return (
      <ChartModeRoot>
        <ModeGate modeKey="chart">
          <ChartScreen
            api={api}
            company={company}
            {...(overlay ? { overlay } : {})}
            onOverlay={(next) =>
              void navigate({
                to: ".",
                search: (prev: Record<string, unknown>) => {
                  const { overlay: _drop, ...rest } = prev;
                  return next === "structure" ? rest : { ...rest, overlay: next };
                },
                replace: true,
              } as never)
            }
            {...(isLens(lens) ? { lens } : {})}
            {...(seat ? { seat } : {})}
            {...(move && under
              ? { stage: { seat: move, to: under === "top" ? null : under } }
              : {})}
            onStaged={() =>
              void navigate({
                to: ".",
                search: (prev: Record<string, unknown>) => {
                  const { move: _m, under: _u, ...rest } = prev;
                  return rest;
                },
                replace: true,
              } as never)
            }
            onLens={(next) =>
              void navigate({
                to: ".",
                search: (prev: Record<string, unknown>) => ({ ...prev, lens: next }),
                replace: true,
              } as never)
            }
          />
        </ModeGate>
      </ChartModeRoot>
    );
  },
});

export const chartSeatRoute = createRoute({
  getParentRoute: () => chartRoute,
  path: "seat/$id",
  validateSearch: (search: Record<string, unknown>) => {
    const raw = typeof search.tab === "string" ? search.tab : "";
    const tab = (SEAT_TABS as readonly string[]).includes(raw)
      ? (raw as SeatTab)
      : SEAT_TAB_ALIASES[raw];
    return {
      ...validateFocusSearch(search),
      ...(tab && tab !== "responsibilities" ? { tab } : {}),
    };
  },
  // The seat page writes the playhead: the scorecard, Agents and the chart keep this seat in focus.
  beforeLoad: ({ params, location }) => {
    if (!ID.test(params.id)) return;
    const href = reconcileHref(location, { subject: { kind: "seat", id: params.id } });
    if (href) throw redirect({ href, replace: true });
  },
  component: function ChartSeat() {
    const { api } = chartSeatRoute.useRouteContext();
    const { id } = chartSeatRoute.useParams();
    const { tab } = chartSeatRoute.useSearch();
    const navigate = useNavigate();
    return (
      <ModeGate modeKey="chart">
        <SeatPage
          api={api}
          company={useCompany()}
          id={id}
          {...(tab ? { tab } : {})}
          onTab={(next) =>
            void navigate({
              to: ".",
              search: (prev: Record<string, unknown>) => {
                const { tab: _drop, ...rest } = prev;
                return next === "responsibilities" ? rest : { ...rest, tab: next };
              },
            } as never)
          }
        />
      </ModeGate>
    );
  },
});

/** The old being-built page's URL: the editor is the chart now. */
export const chartEditorRoute = createRoute({
  getParentRoute: () => chartRoute,
  path: "editor",
  beforeLoad: ({ params, search }) => {
    throw redirect({
      to: "/c/$slug/kpis/chart",
      params: { slug: (params as { slug: string }).slug },
      search: validateFocusSearch(search as Record<string, unknown>),
      replace: true,
    } as never);
  },
});
